import { useState, useEffect } from "react";
import {
  Search, Plus, Pencil, Trash2, User,
} from "lucide-react";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import Card from "../../../components/ui/Card";
import {
  Select, Modal, Form, Input, InputNumber, DatePicker,
  Button, Tag, Popconfirm, Table, message, Spin, Tooltip, Skeleton,
} from "antd";
import dayjs from "dayjs";
import {
  useFieldWorkerList,
  useFieldWorkerDetail,
  useFieldWorkerCreate,
  useFieldWorkerUpdate,
  useFieldWorkerDelete,
  useMasterCertifications,
  useMasterCompliances,
} from "../../../hooks/api/field-workers/hooks";
import {
  IFieldWorker,
  IFieldWorkerCreatePayload,
  IFieldWorkerUpdatePayload,
} from "../../../types/api/fieldWorker";
import { useTerm } from "../../../lib/terminology";

const EMP_COLOR: Record<string, string> = {
  employee: "blue",
  subcontractor: "purple",
  contractor: "geekblue",
  casual: "orange",
};

const EMP_OPTS = [
  { value: "employee", label: "Employee" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "contractor", label: "Contractor" },
  { value: "casual", label: "Casual" },
];

function fullName(w: IFieldWorker) {
  return [w.first_name, w.last_name].filter(Boolean).join(" ") || "—";
}

function initials(w: IFieldWorker) {
  return [w.first_name?.[0], w.last_name?.[0]].filter(Boolean).join("").toUpperCase() || "?";
}

// ── Worker Form Modal ─────────────────────────────────────────────────────────

function WorkerModal({
  open,
  editing,
  onClose,
  onSuccess,
}: {
  open: boolean;
  editing?: IFieldWorker | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const term = useTerm();
  const [form] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();
  const isEdit = !!editing;

  // Fetch full detail (includes certifications/compliances) when editing
  const { data: detailRes, isLoading: detailLoading } = useFieldWorkerDetail(editing?.id ?? null);
  const detailWorker = detailRes?.data ?? null;

  const { data: certRes } = useMasterCertifications();
  const { data: compRes } = useMasterCompliances();
  const certOptions = (certRes?.data ?? []).map((c) => ({ value: c.id, label: c.name }));
  const compOptions = (compRes?.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  const { mutate: create, isPending: creating } = useFieldWorkerCreate({
    onSuccess: () => { messageApi.success("Field worker created."); onSuccess(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  const { mutate: update, isPending: updating } = useFieldWorkerUpdate({
    onSuccess: () => { messageApi.success("Field worker updated."); onSuccess(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  // Hydrate form when detail data arrives (only once per worker load)
  useEffect(() => {
    if (!isEdit || !detailWorker || detailWorker.id !== editing?.id) return;
    const worker = detailWorker as any;
    form.setFieldsValue({
      first_name: worker.first_name,
      last_name: worker.last_name,
      email: worker.email,
      phone: worker.phone,
      mobile: worker.mobile,
      job_title: worker.job_title,
      employee_number: worker.employee_number,
      employment_type: worker.employment_type ?? "employee",
      hourly_rate: worker.hourly_rate,
      postcode: worker.postcode,
      start_date: worker.start_date ? dayjs(worker.start_date) : null,
      end_date: worker.end_date ? dayjs(worker.end_date) : null,
      status: worker.status,
      certification_ids: worker.certifications?.map((c: any) => c.certification_id) ?? [],
      compliance_ids: worker.compliances?.map((c: any) => c.compliance_id) ?? [],
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailWorker?.id]);

  const handleOpen = (v: boolean) => {
    if (!v) return;
    if (!isEdit) {
      form.resetFields();
      form.setFieldsValue({ employment_type: "employee", status: "ACTIVE" });
    }
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const payload = {
        ...values,
        start_date: values.start_date ? values.start_date.format("YYYY-MM-DD") : null,
        end_date: values.end_date ? values.end_date.format("YYYY-MM-DD") : null,
        certification_ids: values.certification_ids ?? [],
        compliance_ids: values.compliance_ids ?? [],
      };
      if (isEdit && editing) {
        update({ ...payload, id: editing.id } as IFieldWorkerUpdatePayload);
      } else {
        create(payload as IFieldWorkerCreatePayload);
      }
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterOpenChange={handleOpen}
      title={isEdit ? `Edit — ${fullName(editing!)}` : `Add ${term("worker")}`}
      width={640}
      footer={null}
      destroyOnClose
    >
      {ctx}
      {isEdit && detailLoading && <Skeleton active paragraph={{ rows: 4 }} className="mt-2 mb-2" />}
      {/* autocomplete="off" prevents browser from autofilling admin credentials */}
      <Form form={form} layout="vertical" size="small" className="mt-2" autoComplete="off" style={{ display: isEdit && detailLoading ? "none" : undefined }}>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="last_name" label="Last Name">
            <Input autoComplete="off" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email", message: "Valid email is required" }]}>
            <Input placeholder="worker@example.com" autoComplete="new-email" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input autoComplete="off" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="mobile" label="Mobile">
            <Input autoComplete="off" />
          </Form.Item>
          {!isEdit && (
            <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
              <Input.Password placeholder="Min 6 characters" autoComplete="new-password" />
            </Form.Item>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="employment_type" label="Employment Type">
            <Select options={EMP_OPTS} />
          </Form.Item>
          <Form.Item name="job_title" label="Job Title">
            <Input placeholder="e.g. Fire Safety Inspector" autoComplete="off" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="employee_number" label="Employee Number">
            <Input placeholder="EMP-001" autoComplete="off" />
          </Form.Item>
          <Form.Item name="postcode" label="Postcode">
            <Input placeholder="SW1A 1AA" autoComplete="off" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="hourly_rate" label="Hourly Rate (£)">
            <InputNumber min={0} step={0.01} prefix="£" className="w-full" />
          </Form.Item>
          <Form.Item name="start_date" label="Start Date">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
        </div>
        {isEdit && (
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="end_date" label="End Date">
              <DatePicker className="w-full" format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="status" label="Status">
              <Select options={[{ value: "ACTIVE", label: "Active" }, { value: "INACTIVE", label: "Inactive" }]} />
            </Form.Item>
          </div>
        )}

        <Form.Item name="certification_ids" label="Certifications">
          <Select mode="multiple" options={certOptions} placeholder="Select certifications…" allowClear />
        </Form.Item>
        <Form.Item name="compliance_ids" label="Compliances">
          <Select mode="multiple" options={compOptions} placeholder="Select compliances…" allowClear />
        </Form.Item>

        <div className="flex justify-end gap-2 mt-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={creating || updating}
            onClick={handleSubmit}
          >
            {isEdit ? "Save" : "Create Worker"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const FieldWorkersPage = () => {
  const term = useTerm();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWorker, setEditingWorker] = useState<IFieldWorker | null>(null);
  const [messageApi, ctx] = message.useMessage();

  const {
    data: listData,
    isLoading,
    refetchWithQuery,
  } = useFieldWorkerList({ page, limit: 20 });

  const workers: IFieldWorker[] = listData?.data ?? [];
  const total = Number((listData as any)?.extra?.total ?? 0);

  const { mutate: deleteWorker } = useFieldWorkerDelete({
    onSuccess: () => { messageApi.success("Field worker deleted."); refetchWithQuery({ page }); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  const activeCount = workers.filter((w) => w.status === "ACTIVE").length;

  const columns = [
    {
      title: "Worker",
      key: "worker",
      render: (_: any, w: IFieldWorker) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ background: "rgba(1,59,190,0.1)", color: "var(--color-primary)" }}>
            {initials(w)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800 text-sm">{fullName(w)}</span>
              {w.employee_number && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "rgba(1,59,190,0.1)", color: "var(--color-primary)" }}>
                  {w.employee_number}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-400">{w.job_title || "—"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_: any, w: IFieldWorker) => (
        <div className="text-xs text-slate-500">
          {w.email && <div>{w.email}</div>}
          {w.phone && <div>{w.phone}</div>}
          {w.mobile && <div>{w.mobile}</div>}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "employment_type",
      key: "employment_type",
      render: (v: string) => v ? <Tag color={EMP_COLOR[v] ?? "default"}>{v.replace(/_/g, " ")}</Tag> : "—",
    },
    {
      title: "Rate",
      dataIndex: "hourly_rate",
      key: "hourly_rate",
      render: (v: number | null) => v ? `£${Number(v).toFixed(2)}/hr` : "—",
    },
    {
      title: "Certs / Compliance",
      key: "certs",
      render: (_: any, w: IFieldWorker) => {
        const certCount = Number(w.certification_count ?? 0);
        const compCount = Number(w.compliance_count ?? 0);
        return (
          <div className="flex gap-1.5">
            <Tag color={certCount > 0 ? "blue" : "default"} className="text-xs">
              {certCount} cert{certCount !== 1 ? "s" : ""}
            </Tag>
            <Tag color={compCount > 0 ? "purple" : "default"} className="text-xs">
              {compCount} compliance{compCount !== 1 ? "s" : ""}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => (
        <Tag color={v === "ACTIVE" ? "green" : "default"}>{v}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, w: IFieldWorker) => (
        <div className="flex items-center gap-1">
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<Pencil size={11} />}
              onClick={() => { setEditingWorker(w); setModalOpen(true); }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this field worker?"
            onConfirm={() => deleteWorker(w.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button size="small" icon={<Trash2 size={11} />} danger type="text" />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen">
      {ctx}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">{term("worker", true)}</h1>
          <p className="text-slate-500 text-sm">{total} total · {activeCount} active</p>
        </div>
        <ButtonWrapper
          type="primary"
          icon={<Plus size={14} />}
          onClick={() => { setEditingWorker(null); setModalOpen(true); }}
          className="flex items-center gap-2"
        >
          Add {term("worker")}
        </ButtonWrapper>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="text-slate-400 absolute left-3 top-2.5 z-10" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              refetchWithQuery({ search: e.target.value, page: 1 });
              setPage(1);
            }}
            placeholder="Search workers…"
            className="pl-9 pr-4 h-10 text-sm border border-slate-200 rounded-lg w-full bg-white focus:outline-none focus:border-primary"
          />
        </div>
        <Select
          placeholder="Status"
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            refetchWithQuery({ status: v, page: 1 });
            setPage(1);
          }}
          allowClear
          className="w-44"
          options={[
            { value: "ACTIVE", label: "Active" },
            { value: "INACTIVE", label: "Inactive" },
          ]}
        />
      </div>

      <Card className="overflow-hidden">
        <Table
          dataSource={workers}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: 20,
            total,
            showSizeChanger: false,
            showTotal: (t) => `${t} workers`,
            onChange: (p) => { setPage(p); refetchWithQuery({ page: p }); },
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      <WorkerModal
        open={modalOpen}
        editing={editingWorker}
        onClose={() => { setModalOpen(false); setEditingWorker(null); }}
        onSuccess={() => {
          setModalOpen(false);
          setEditingWorker(null);
          refetchWithQuery({ page });
        }}
      />
    </div>
  );
};

export default FieldWorkersPage;
